import React, { useMemo, useState } from "react";
import {
  Sparkles,
  Check,
  CreditCard,
  CheckCircle,
  Info,
  Target,
  FileText,
  Loader,
  Calculator,
  X,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import {
  setIsChatReferralLetter,
  setChatBotReferralLetter,
  setChatQuestions,
  setAddPatientAIChatMessage,
  setUpdateVitalAIChatMessage,
  setAiChatSuccessMessages,
  setVitalsDetails,
  updateConsultationActions,
  deleteConsultationAction,
  SaveAIChatMessage,
  setPatientAIChatMessagesFromConsultationPayload,
  setIncrementTreatmentPlanCompleted,
  updateTaskActionStatus,
  setLetterDetails,
  setReferralLetter,
  setDynamicPrescriptions,
  setBookingDetails,
  addPatientVitals,
  getPatientVitals,
  GetPatientVitalsSummaryByPatientId,
  getPatientConsultationByConsultationId,
  setIncrementVitalsCompleted,
  setCompletedTasksForConsultation,
  toggleCompletedTaskForConsultation,
} from "@/dispatchers/patientDispatchers";
import {
  setShowBookingSlots,
  setBookingSlotsMode,
  setShowBacktoSlotsButton,
} from "@/dispatchers/scheduleEventsDispatchers";
import {
  setShowPermissionModal,
  setShowPermissionMessage,
} from "@/dispatchers/appDispatchers";
import { checkUserPermissions } from "@/utils/checkUserPermissions";
import { accessTitanverseCalenderUpsert } from "@/data/constantStrings";
import useTitanApp from "../../../../../hooks/useTitanApp";
import { motion } from "framer-motion";
import { FiArrowRight } from "react-icons/fi";
// @ts-ignore - project may not have image module declarations for .png files
import avatarPlaceholder from "./../../../../../assets/images/avatar-placeholder.png";
// @ts-ignore - project may not have image module declarations for .svg files
import aiGlobe from "./../../../../../assets/images/ai-globe.svg";
import { ActionGroupCard } from "./ActionGroupCard/ActionGroupCard";
import { Badge } from "@/components/ui/badge";
// import { CalculatorWidgets } from "./CalculatorWidgets";
import { CalculatorWidget } from "./CalculatorWidget";
import { CalculatorResult, DosageCalculator } from "../dosageCalculators/types";
import { NEWS2Calculator } from "@/components/clinical-risk-calculators/NEWS2Calculator";
import { patientConsultationSignalR } from "../../../../../services/patientConsultationSignalR";
import { LinkTreatmentPlanDialog } from "@/components/PatientDashboardV2/components/LinkTreatmentPlanDialog";
import {
  setLinkTreatmentPlanDialog,
  setLinkedTreatmentPlan,
  clearTreatmentPlanDraft,
  saveTreatmentPlanDraft,
  generateAITreatmentPlan,
} from "@/dispatchers/treatmentPlanDispatchers";
import { TreatmentPlanDraftActionItem } from "./ActionGroupCard/TreatmentPlanDraftActionItem";
import { TreatmentPlanModal } from "../../TreatmentPlan/TreatmentPlanModal";
import { format } from "date-fns";
import { showError } from "@/components/ToastedMessage/ToastedMessage";
import { notificationsHub } from "@/services/notificationsHub";
import { ActionType } from "@/enum/ActionType";

// Enum to track the lifecycle status of a draft action
enum DraftStatus {
  Pending = "pending",
  Completed = "completed",
}

interface CompletedAction {
  id: string;
  type:
    | "prescription"
    | "appointment"
    | "vaccine"
    | "referral"
    | "payment"
    | "vitals"
    | "document"
    | "image"
    | "signature"
    | "prescription-draft"
    | "vaccine-draft";
  timestamp: Date;
  data: any;
  actionNumber?: number;
  actionName?: string;
  isCompleted?: boolean;
}

interface ActionsPanelProps {
  isDarkMode: boolean;
  pharmacistActions: CompletedAction[];
  selectedPatientDetails: any;
  patientSummary: any;
  prescriptionCompleted: boolean;
  paymentCompleted: boolean;
  referralCompleted: boolean;
  bookingCompleted: boolean;
  vitalsCompleted: boolean;
  vaccineCompleted: boolean;
  isConsultationEnabled: boolean;
  onDeleteAction: (actionId: string) => void;
  setPharmacistActions: React.Dispatch<React.SetStateAction<CompletedAction[]>>;
  setVitals?: React.Dispatch<React.SetStateAction<any[]>>;
  onActionComplete: (actionType: string, data: any) => void;
  getActionIconById: (id: number, isCompleted: boolean) => React.ReactNode;
  getActions: (actionNumber: string | number) => void;
  patientAIChatMessages?: any[];
  patientChatAnswersLoader?: boolean;
  isTyping?: boolean;
  chatBoxRef?: React.RefObject<HTMLDivElement>;
  completedConsultationActions: any;
  onRemoveManualCalculator?: (calculatorId: string) => void;
  manualCalculators?: DosageCalculator[];
  selectedPatient: any;
}

const ACTION_GROUP_CONFIG = {
  1: { key: "prescriptions", title: "Prescriptions" },
  2: { key: "payments", title: "Payments" },
  3: { key: "referrals", title: "Referrals" },
  4: { key: "appointments", title: "Appointments" },
  5: { key: "vitals", title: "Vitals" },
  6: { key: "vaccinations", title: "Vaccinations" },
  8: { key: "documents", title: "Documents" },
  9: { key: "signatures", title: "Signatures" },
  13: { key: "images", title: "Images" },
  // drafts
  11: { key: "draft-prescriptions", title: "Draft Prescriptions" },
  12: { key: "draft-vaccinations", title: "Draft Vaccinations" },
  "prescription-draft": {
    key: "draft-prescriptions",
    title: "Draft Prescriptions",
  },
  "vaccine-draft": { key: "draft-vaccinations", title: "Draft Vaccinations" },
  15: { key: "treatment-plans", title: "Treatment Plans" },
};

export const ActionsPanel: React.FC<ActionsPanelProps> = ({
  isDarkMode,
  pharmacistActions,
  selectedPatientDetails,
  patientSummary,
  prescriptionCompleted,
  paymentCompleted,
  referralCompleted: _referralCompleted,
  bookingCompleted,
  vitalsCompleted,
  vaccineCompleted,
  isConsultationEnabled,
  onDeleteAction,
  setPharmacistActions,
  setVitals,
  onActionComplete,
  getActionIconById,
  getActions,
  patientAIChatMessages = [],
  patientChatAnswersLoader = false,
  isTyping = false,
  chatBoxRef,
  completedConsultationActions,
  onRemoveManualCalculator,
  manualCalculators = [],
  selectedPatient,
}) => {
  const { toast } = useToast();
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const {
    appState,
    patientState,
    templateState,
    treatmentPlanState,
  } = useTitanApp();
  const {
    signatureDetails,
    vitalsDetails,
    vaccineDetails,
    bookingDetails,
    prescriptionDetails,
    signatureCompleted,
    paymentAuditData,
    isEditMode,
    consultationId,
    isImageDocUploaded,
    imageDocDetails,
    consultationData,
    selectedPatientConsultation,
    isTreatmenPlanCreated,
    treatmentPlanDetails,
    patientSummary: patientSummaryFromState,
  } = patientState || {};
  const {
    patientActiveTreatmentPlans,
    isLinkTreatmentPlanDialogOpen,
    linkedTreatmentPlanId,
    treatmentPlanDrafts,
  } = treatmentPlanState || {};
  const currentTreatmentPlanDraft = consultationId
    ? treatmentPlanDrafts?.[consultationId] ?? null
    : null;
  const { selectedTemplate } = templateState || {};
  const completedTaskLabels: string[] = consultationId
    ? patientState?.completedTasksByConsultation?.[consultationId] ?? []
    : [];
  const completedTreatmentActions = new Set<string>(completedTaskLabels);
  const [isGeneratingAIPlan, setIsGeneratingAIPlan] = useState(false);
  const [draftVaccineCount, setDraftVaccineCount] = useState(0);
  const [
    showTreatmentPlanDraftModal,
    setShowTreatmentPlanDraftModal,
  ] = useState(false);
  const [isNews2Saving, setIsNews2Saving] = useState(false);

  React.useEffect(() => {
    const completedTasks = new Set<string>();
    let hasPersistedTaskState = false;

    // Source 1: Check consultation template actions (fallback/default source)
    const consultationActions =
      selectedPatientConsultation?.template?.consultationActions || [];
    consultationActions.forEach((action: any) => {
      if (
        action.label &&
        (action.isCompleted === true ||
          action.enabled === true ||
          action.completed === true)
      ) {
        completedTasks.add(action.label);
      }
    });

    // Source 2: Check AI chat messages (actionId 14) - persisted task payload
    if (patientAIChatMessages?.length) {
      const taskMessages = patientAIChatMessages.filter(
        (msg: any) => String(msg.actionId) === "14",
      );
      taskMessages.forEach((msg: any) => {
        if (msg.details?.payload) {
          try {
            const payload = JSON.parse(msg.details.payload);
            const tasks = payload?.tasks;
            if (Array.isArray(tasks)) {
              hasPersistedTaskState = true;
              tasks.forEach((task: any) => {
                if (
                  task.label &&
                  (task.enabled === true ||
                    task.completed === true ||
                    task.isCompleted === true)
                ) {
                  completedTasks.add(task.label);
                }
              });
            }
          } catch (e) {
            console.error("Error parsing task payload:", e);
          }
        }
      });
    }

    // Source 3: Consultation actions CreateTask payload - persisted task payload
    const createTaskAction = (consultationData?.actions || []).find(
      (a: any) => a?.actionName === "CreateTask" && a?.payload,
    );
    if (createTaskAction?.payload) {
      try {
        const payload = JSON.parse(createTaskAction.payload);
        const tasks = payload?.tasks;
        if (Array.isArray(tasks)) {
          hasPersistedTaskState = true;
          tasks.forEach((task: any) => {
            if (
              task.label &&
              (task.enabled === true ||
                task.completed === true ||
                task.isCompleted === true)
            ) {
              completedTasks.add(task.label);
            }
          });
        }
      } catch (e) {
        console.error("Error parsing consultation CreateTask payload:", e);
      }
    }

    if (!consultationId) return;
    const hasBeenSeeded =
      consultationId in (patientState?.completedTasksByConsultation ?? {});
    if (hasBeenSeeded) return;
    setCompletedTasksForConsultation(
      consultationId,
      Array.from(completedTasks),
    );
  }, [selectedPatientConsultation, patientAIChatMessages, consultationData]);

  React.useEffect(() => {
    if (!consultationId) return;

    let unsubscribe: (() => Promise<void>) | undefined;
    let isDisposed = false;

    const subscribeToMergeResult = async () => {
      try {
        const cleanup = await notificationsHub.subscribeMergeResult(
          consultationId,
          async (payload) => {
            if (isDisposed) return;

            const payloadId =
              payload?.ConsultationId ??
              payload?.consultationId ??
              payload?.TransactionId ??
              payload?.transactionId;
            if (payloadId && String(payloadId) !== String(consultationId))
              return;

            const data = {
              ...payload,
              json:
                typeof payload?.json === "string"
                  ? JSON.parse(payload.json)
                  : payload?.json ?? null,
            };

            setPatientAIChatMessagesFromConsultationPayload(data);

            toast({
              title: "Actions successful ✅",
              description:
                payload?.message || "Actions have been successfully merged.",
              duration: 8000,
            });
          },
          (errorMessage) => {
            if (isDisposed) return;

            showError(
              errorMessage || "Actions failed. Please contact support.",
              true,
              6000,
            );
          },
        );

        if (isDisposed) {
          await cleanup();
          return;
        }

        unsubscribe = cleanup;
      } catch (error) {
        if (isDisposed) return;

        console.error("Failed to get actions:", error);

        showError(
          "Unable to get actions. Please check your internet connection and try again.",
          true,
          5000,
        );
      }
    };

    subscribeToMergeResult();

    return () => {
      isDisposed = true;
      unsubscribe?.();
    };
  }, [consultationId]);

  React.useEffect(() => {
    setShowTreatmentPlanDraftModal(false);
  }, [consultationId]);

  React.useEffect(() => {
    const scrollToBottom = () => {
      if (scrollRef.current) {
        scrollRef.current.scrollIntoView({ behavior: "smooth" });
      }
    };
    const raf = requestAnimationFrame(scrollToBottom);
    return () => cancelAnimationFrame(raf);
  }, [patientAIChatMessages]);

  // Auto-detect linked treatment plan from followup consultationIDs.
  // When returning to a consultation that was previously linked via appointment,
  // Redux state is gone — so we re-derive it from the active plans response.
  React.useEffect(() => {
    if (!consultationId) return;
    if (!patientActiveTreatmentPlans?.length) return;
    if (linkedTreatmentPlanId) return; // already linked, skip

    const matchedPlan = patientActiveTreatmentPlans.find((plan: any) => {
      const followUps: any[] = plan.followUpAppointments || [];
      return followUps.some(
        (apt: any) =>
          apt.consultationID === consultationId ||
          apt.consultationId === consultationId,
      );
    });

    if (matchedPlan) {
      const planId =
        matchedPlan.treatmentPlanId || matchedPlan._id || matchedPlan.id || "";
      if (planId) {
        setLinkedTreatmentPlan(planId);
      }
    }
  }, [consultationId, patientActiveTreatmentPlans, linkedTreatmentPlanId]);

  // React.useEffect(() => {
  //   if (prescriptionCompleted) {
  //     const deserializedPayload = JSON.parse(prescriptionDetails.payload);
  //     const payloads = patientAIChatMessages
  //       .filter((item) => item.actionId === 1)
  //       .map((item) => JSON.parse(item.details.payload));

  //     const exists = payloads.some(
  //       (msg) =>
  //         msg.prescriptionDetails?.prescriptionId ===
  //         deserializedPayload?.prescriptionDetails?.prescriptionId,
  //     );
  //     if (!exists) {
  //       setAddPatientAIChatMessage({
  //         sender: "success",
  //         message: "Prescription completed successfully.",
  //         actionId: 1,
  //         details: prescriptionDetails,
  //       });
  //     }

  //     // Delete any pending prescription draft (Action ID 11) now that
  //     // the actual prescription has been completed.
  //     const pendingDraft = patientAIChatMessages.find(
  //       (msg) =>
  //         (msg.actionId === 11 || msg.actionId === "11") &&
  //         msg.sender === "success",
  //     );

  //     if (pendingDraft) {
  //       deleteConsultationAction({
  //         consultationID: consultationId,
  //         actionNumber: "11",
  //       }).catch((err) =>
  //         console.warn("Failed to delete prescription draft:", err),
  //       );
  //     }

  //     // Also clear from local pharmacistActions if any
  //     setPharmacistActions((prev) =>
  //       prev.filter((a) => a.type !== "prescription-draft"),
  //     );
  //   }
  // }, [prescriptionCompleted, prescriptionDetails]);

  // React.useEffect(() => {
  //   if (paymentCompleted) {
  //     setAddPatientAIChatMessage({
  //       sender: "success",
  //       message: "Payment completed successfully.",
  //       actionId: 2,
  //     });
  //   }
  // }, [paymentCompleted]);

  // React.useEffect(() => {
  //   if (bookingCompleted && bookingDetails != null) {
  //     // Check if this booking already exists in messages
  //     const exists = patientAIChatMessages.some((msg) => {
  //       if (msg.actionId !== 4) return false;

  //       // If we have booking details with an ID, check for that
  //       if (bookingDetails?.id && msg.details?.payload) {
  //         try {
  //           const payload = JSON.parse(msg.details.payload);
  //           return payload.id === bookingDetails.id;
  //         } catch (e) {
  //           return false;
  //         }
  //       }

  //       return msg.message === "Booking completed successfully.";
  //     });

  //     if (!exists) {
  //       setAddPatientAIChatMessage({
  //         sender: "success",
  //         message: "Booking completed successfully.",
  //         actionId: 4,
  //         details: bookingDetails,
  //       });
  //     }
  //   }
  // }, [bookingCompleted, bookingDetails, patientAIChatMessages]);

  React.useEffect(() => {
    if (!vitalsCompleted) return;
    if (!vitalsDetails) return;

    let parsedVitals = vitalsDetails;

    try {
      if (vitalsDetails.payload && typeof vitalsDetails.payload === "string") {
        const parsedPayload = JSON.parse(vitalsDetails.payload);
        if (parsedPayload?.vitalsDetails) {
          parsedVitals = parsedPayload.vitalsDetails;
        } else {
          parsedVitals = parsedPayload;
        }
      } else if (typeof vitalsDetails === "string") {
        const parsed = JSON.parse(vitalsDetails);
        parsedVitals = parsed?.vitalsDetails || parsed;
      }
    } catch (err) {
      console.warn(" Error parsing vitalsDetails:", err);
      parsedVitals = vitalsDetails;
    }

    let recordedVitalType = null;

    if (parsedVitals.bloodPressure) recordedVitalType = "bloodPressure";
    else if (parsedVitals.temperature) recordedVitalType = "temperature";
    else if (parsedVitals.respiratoryRate)
      recordedVitalType = "respiratoryRate";
    else if (parsedVitals.circumference) recordedVitalType = "circumference";
    else if (parsedVitals.heartRate) recordedVitalType = "heartRate";
    else if (parsedVitals.bmi && parsedVitals.weight && parsedVitals.height)
      recordedVitalType = "bmi";
    else if (parsedVitals.weight && !parsedVitals.height)
      recordedVitalType = "weight";
    else if (parsedVitals.height && !parsedVitals.weight)
      recordedVitalType = "height";

    const newMessage = {
      sender: "success",
      message: "Vitals recorded successfully.",
      actionId: 5,
      details: {
        payload: JSON.stringify({
          vitalsDetails: { ...parsedVitals, recordedVitalType },
        }),
      },
      vitalMessageId:
        vitalsDetails?.vitalMessageId ||
        `vital-${Date.now()}-${Math.random()
          .toString(36)
          .substr(2, 9)}`,
    };

    const existingVitalIndex = patientAIChatMessages.findIndex((msg) => {
      if (msg.actionId === 5) {
        if (vitalsDetails?.vitalMessageId) {
          const match = msg.vitalMessageId === vitalsDetails.vitalMessageId;
          return match;
        }
        try {
          const msgPayload = JSON.parse(msg.details?.payload || "{}");
          const msgVitalType = msgPayload?.vitalsDetails?.recordedVitalType;
          return msgVitalType === recordedVitalType;
        } catch (e) {
          return false;
        }
      }
      return false;
    });

    if (existingVitalIndex !== -1 && vitalsDetails?.vitalMessageId) {
      setUpdateVitalAIChatMessage(newMessage);
    } else {
      setAddPatientAIChatMessage(newMessage);
    }

    setVitalsDetails(null);
  }, [vitalsCompleted, vitalsDetails, isEditMode]);

  // React.useEffect(() => {
  //   if (vaccineCompleted) {
  //     const exists = patientAIChatMessages.some(
  //       (msg) => msg.details === vaccineDetails,
  //     );
  //     if (!exists) {
  //       setAddPatientAIChatMessage({
  //         sender: "success",
  //         message: "Vaccine administered successfully.",
  //         actionId: 6,
  //         details: vaccineDetails,
  //       });
  //     }

  //     // Delete any pending vaccine draft (Action ID 12) now that
  //     // a vaccination has been completed.
  //     const pendingDraft = patientAIChatMessages.find(
  //       (msg) =>
  //         (msg.actionId === 12 || msg.actionId === "12") &&
  //         msg.sender === "success",
  //     );

  //     if (pendingDraft) {
  //       deleteConsultationAction({
  //         consultationID: consultationId,
  //         actionNumber: "12",
  //       }).catch((err) => console.warn("Failed to delete vaccine draft:", err));
  //     }

  //     // Also clear from local pharmacistActions if any
  //     setPharmacistActions((prev) =>
  //       prev.filter((a) => a.type !== "vaccine-draft"),
  //     );
  //   }
  // }, [vaccineCompleted, vaccineDetails]);

  // React.useEffect(() => {
  //   if (signatureCompleted) {
  //     const exists = patientAIChatMessages.some(
  //       (msg) =>
  //         msg.details?.patientSignature === signatureDetails?.patientSignature,
  //     );
  //     if (!exists) {
  //       setAddPatientAIChatMessage({
  //         sender: "success",
  //         message: "Signature",
  //         actionId: 9,
  //         details: signatureDetails,
  //       });
  //     }
  //   }
  // }, [signatureCompleted, signatureDetails]);

  React.useEffect(() => {
    if (isImageDocUploaded) {
      setAddPatientAIChatMessage({
        sender: "success",
        message: "Media uploaded successfully.",
        actionId: 13,
        details: imageDocDetails,
      });
    }
  }, [isImageDocUploaded, imageDocDetails]);

  // React.useEffect(() => {
  //   if (isTreatmenPlanCreated) {
  //     setAddPatientAIChatMessage({
  //       sender: "success",
  //       message: "Treatment Plan Created Successfully",
  //       actionId: 15,
  //       details: treatmentPlanDetails,
  //     });
  //   }
  //   setIncrementTreatmentPlanCompleted(false);
  // }, [isTreatmenPlanCreated, treatmentPlanDetails]);
  const suggestedActions = patientSummary?.Actions || [];
  const auditData = paymentAuditData || {};
  const serviceChargesType = auditData?.serviceChargesType ?? 1;

  const buildReferralLetterFromSummary = (summary: any) => {
    const patientName =
      selectedPatientDetails?.patientName ||
      `${selectedPatientDetails?.foreName ||
        ""} ${selectedPatientDetails?.surName || ""}`.trim() ||
      "the patient";

    const today = new Date().toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });

    const presentingComplaint = summary?.["Presenting Complaint"];
    const medicalHistory = summary?.["Medical History"];
    const familyHistory = summary?.["Family History"];
    const socialHistory = summary?.["Social History"];
    const observations = summary?.["Observations and Assessments"];
    const advice = summary?.["Advice and Counselling Provided"];
    const followUp = summary?.["Follow-Up Plan"];

    const subject =
      presentingComplaint?.toLowerCase?.().includes("blood sugar") ||
      presentingComplaint?.toLowerCase?.().includes("thirst") ||
      presentingComplaint?.toLowerCase?.().includes("urination")
        ? "Referral for assessment of possible diabetes"
        : "Referral for further clinical assessment";

    const content = [
      `Date: ${today}`,
      "",
      "Dear Doctor,",
      "",
      `Re: ${patientName}`,
      "",
      `I am referring ${patientName} for further assessment and management.`,
      "",
      presentingComplaint
        ? `Presenting complaint:\n${presentingComplaint}`
        : null,
      medicalHistory ? `Medical history:\n${medicalHistory}` : null,
      familyHistory ? `Family history:\n${familyHistory}` : null,
      socialHistory ? `Social history:\n${socialHistory}` : null,
      observations ? `Observations and assessment:\n${observations}` : null,
      advice ? `Advice provided:\n${advice}` : null,
      followUp ? `Follow-up plan:\n${followUp}` : null,
      "",
      "I would be grateful if you could review and advise on further investigation and management.",
      "",
      "Kind regards,",
      appState?.currentUser?.fullName || "Clinician",
    ]
      .filter(Boolean)
      .join("\n\n");

    return {
      title: "Referral Letter",
      subject,
      content,
    };
  };

  const handleSuggestionClick = (actionNum: string) => {
    if (actionNum === "3") {
      // Suggestions should open the standard referral flow with fresh state,
      // not inherit chat-generated referral context from a previous action.
      setIsChatReferralLetter(false);
      setChatQuestions("");
      setLetterDetails(null);
      setChatBotReferralLetter(null);
      setReferralLetter(buildReferralLetterFromSummary(patientSummary || {}));
    }
    if (actionNum === "1") {
      setDynamicPrescriptions([
        {
          id: Date.now(),
          value: "",
          quantity: "",
          dose: "",
          notes: "",
          prescribedItemId: "",
        },
      ]);
    }
    if (actionNum === "4") {
      const hasAppointmentPermission = checkUserPermissions(
        accessTitanverseCalenderUpsert,
      );

      if (!hasAppointmentPermission) {
        setShowPermissionMessage("Create or update calender events");
        setShowPermissionModal(true);
        return;
      }

      setBookingSlotsMode("fromAppointment");
      setShowBacktoSlotsButton(true);
      setShowBookingSlots(true);
      setBookingDetails(null);
    }

    getActions(actionNum);
  };

  const paymentStatus = {
    hasDeposit: false,
    hasBookingFee: paymentAuditData?.serviceChargesType === 1,
    bookingFeeAmount:
      paymentAuditData?.serviceChargesType === 1
        ? paymentAuditData.amount / 100
        : undefined,

    hasFullPayment: paymentAuditData?.serviceChargesType === 2,
    fullPaymentAmount:
      paymentAuditData?.serviceChargesType === 2
        ? paymentAuditData.amount / 100
        : undefined,

    totalPaid: paymentAuditData?.amount ? paymentAuditData.amount / 100 : 0,

    paymentMethod: paymentAuditData?.paymentMethod?.card
      ? `${paymentAuditData?.paymentMethod.card.scheme} •••• ${paymentAuditData?.paymentMethod.card.last4}`
      : paymentAuditData?.paymentMethod?.type || "Card",

    paymentDate: paymentAuditData?.createdAt,
  };

  const getPaymentTypeLabel = (paymentStatus: any) => {
    if (paymentStatus.hasFullPayment) return "Full Payment";
    if (paymentStatus.hasBookingFee) return "Booking Fee";
    if (paymentStatus.hasDeposit) return "Deposit";
    return "Payment";
  };

  const getPaymentDetails = (paymentStatus: any) => {
    const details: string[] = [];

    if (paymentStatus.hasDeposit && paymentStatus.depositAmount) {
      details.push(`Deposit: £${paymentStatus.depositAmount.toFixed(2)}`);
    }

    if (paymentStatus.hasBookingFee && paymentStatus.bookingFeeAmount) {
      details.push(
        `Booking Fee: £${paymentStatus.bookingFeeAmount.toFixed(2)}`,
      );
    }

    if (paymentStatus.hasFullPayment && paymentStatus.fullPaymentAmount) {
      details.push(
        `Full Payment: £${paymentStatus.fullPaymentAmount.toFixed(2)}`,
      );
    }

    return details;
  };

  const handleCreatePrescriptionDraft = async (results: CalculatorResult[]) => {
    // Create a draft prescription action
    const draftData = {
      prescriptionItems: results.map((result) => ({
        id: Math.random()
          .toString(36)
          .substr(2, 9),
        value: result.medication,
        strength: result.strength || "",
        medicationType: result.medicationType || "",
        dose: result.dosage,
        quantity: result.quantity,
        notes: result.notes,
        prescribedItemId: result.prescribedItemId || "",
        repeatPrescription: false,
        medicationDispensed: false,
        contraindications: [],
        warnings: [],
      })),
      calculatorResults: results,
      timestamp: new Date().toLocaleString(),
    };

    const payload = {
      payload: JSON.stringify(draftData),
      actionName: "CreatePrescriptionDraft",
      actionNumber: "11",
      isPerformed: true,
      performedAt: new Date().toLocaleString(),
      draftStatus: DraftStatus.Pending,
    };

    try {
      await updateConsultationActions({
        consultationId: consultationId,
        payload: payload,
      });

      // Save as AI chat message for consistency
      await SaveAIChatMessage({
        ...payload,
        consultationId: consultationId,
      }).then(() => {
        setAddPatientAIChatMessage({
          sender: "success",
          message: "Prescription draft created.",
          actionId: 11,
          details: payload,
        });
      });
    } catch (error) {
      console.error("Failed to create prescription draft:", error);
      throw error;
    }
  };

  const handleCreateVaccineDraft = async (results: CalculatorResult[]) => {
    // Create individual draft vaccine actions for each vaccine
    const draftData = {
      vaccineItems: results.map((result) => ({
        vaccineName: result.medication,
        dose: result.dosage,
        schedule: result.duration || "As per schedule",
        notes: result.notes,
        route: "IM / SC",
        site: "Deltoid - left / right",
      })),
      calculatorResults: results,
      timestamp: new Date().toLocaleString(),
    };

    const payload = {
      payload: JSON.stringify(draftData),
      actionName: "CreateVaccineDraft",
      actionNumber: "12",
      isPerformed: true,
      performedAt: new Date().toLocaleString(),
      draftStatus: DraftStatus.Pending,
    };

    try {
      await updateConsultationActions({
        consultationId: consultationId,
        payload: payload,
      });

      // Save as AI chat message for consistency
      await SaveAIChatMessage({
        ...payload,
        consultationId: consultationId,
      }).then(() => {
        setAddPatientAIChatMessage({
          sender: "success",
          message: "Vaccine draft created.",
          actionId: 12,
          details: payload,
        });
      });
    } catch (error) {
      console.error("Failed to create vaccine draft:", error);
      throw error;
    }
  };

  const handleSaveVitals = (vital: any) => {
    // Add vital to patient vitals
    const newVital = {
      id: `vital-${Date.now()}-${Math.random()
        .toString(36)
        .substr(2, 9)}`,
      type: vital.type,
      value: vital.data?.value || vital.value,
      unit: vital.data?.unit || vital.unit || "",
      timestamp: vital.timestamp || new Date(),
    };

    if (setVitals) {
      setVitals((prev) => [...prev, newVital]);
    }
  };

  const handleNEWS2Complete = async (results: CalculatorResult[]) => {
    if (!consultationId || !results[0]?.vitals?.length) {
      onRemoveManualCalculator?.("news2");
      return;
    }

    setIsNews2Saving(true);

    const result = results[0];
    const recordedAt = new Date().toISOString();
    const base = {
      patientId: String(selectedPatientDetails?.patientID || ""),
      patientName: String(selectedPatient || ""),
      consultationId,
      recordedAt,
      serviceName: consultationData?.serviceName,
    };

    // Extract score from vitals array
    const scoreEntry = result.vitals.find((v) => v.type === "NEWS2 score");
    const news2Score = scoreEntry ? parseInt(String(scoreEntry.value)) : 0;
    const riskLabel = result.quantity || "";

    const vitalsToSave: any[] = []; // → addPatientVitals (DB only)
    const news2DisplayVitals: any[] = []; // → summary card display

    for (const vital of result.vitals) {
      if (vital.type === "NEWS2 score") continue;

      if (vital.type === "Respiration rate") {
        vitalsToSave.push({
          ...base,
          vitalType: "Respiratory Rate",
          vitalTypeKey: "respiratoryrate",
          respiratoryRate: {
            Value: String(vital.value),
            Unit: "breaths/min",
            recordedAt,
          },
        });
        news2DisplayVitals.push({
          vitalType: "respiratory-rate",
          label: "Resp Rate",
          value: String(vital.value),
          unit: "bpm",
        });
      } else if (vital.type.startsWith("SpO2")) {
        vitalsToSave.push({
          ...base,
          vitalType: "Oxygen Saturation",
          vitalTypeKey: "oxygensaturation",
          OxygenSaturation: {
            Value: String(vital.value),
            Unit: "%",
            recordedAt,
          },
        });
        news2DisplayVitals.push({
          vitalType: "oxygen-saturation",
          label: "SpO₂",
          value: String(vital.value),
          unit: "%",
        });
      } else if (vital.type === "Supplemental oxygen") {
        const isOnO2 = String(vital.value)
          .toLowerCase()
          .includes("o2");
        const mapped = isOnO2 ? "Yes" : "No";
        vitalsToSave.push({
          ...base,
          vitalType: "Supplemental Oxygen",
          vitalTypeKey: "supplementaloxygen",
          SupplementalOxygen: { Value: mapped, Unit: "", recordedAt },
        });
        news2DisplayVitals.push({
          vitalType: "supplemental-oxygen",
          label: "Suppl. O₂",
          value: mapped,
          unit: "",
        });
      } else if (vital.type === "Systolic BP") {
        // vital.value is "120/80" (full BP string from updated calculator)
        const parts = String(vital.value).split("/");
        const systolicVal = parts[0]?.trim() || "";
        const diastolicVal = parts[1]?.trim() || "";
        vitalsToSave.push({
          ...base,
          vitalType: "Blood Pressure",
          vitalTypeKey: "bloodpressure",
          bloodPressure: {
            Systolic: { Value: systolicVal, Unit: "mmHg", recordedAt },
            Diastolic: { Value: diastolicVal, Unit: "mmHg", recordedAt },
            recordedAt,
          },
        });
        news2DisplayVitals.push({
          vitalType: "blood-pressure",
          label: "BP",
          value: String(vital.value),
          unit: "mmHg",
        });
      } else if (vital.type === "Pulse") {
        vitalsToSave.push({
          ...base,
          vitalType: "Heart Rate",
          vitalTypeKey: "heartrate",
          heartRate: { Value: String(vital.value), Unit: "BPM", recordedAt },
        });
        news2DisplayVitals.push({
          vitalType: "heart-rate",
          label: "Pulse",
          value: String(vital.value),
          unit: "bpm",
        });
      } else if (vital.type === "Consciousness (ACVPU)") {
        const capitalised = vital.value
          ? vital.value.charAt(0).toUpperCase() + vital.value.slice(1)
          : "Alert";
        vitalsToSave.push({
          ...base,
          vitalType: "Consciousness (AVPU)",
          vitalTypeKey: "consciousness(avpu)",
          Consciousness: { Value: capitalised, Unit: "", recordedAt },
        });
        news2DisplayVitals.push({
          vitalType: "consciousness-level",
          label: "Consciousness",
          value: capitalised,
          unit: "",
        });
      } else if (vital.type === "Temperature") {
        vitalsToSave.push({
          ...base,
          vitalType: "Temperature",
          vitalTypeKey: "temperature",
          temperature: { Value: String(vital.value), Unit: "°C", recordedAt },
        });
        news2DisplayVitals.push({
          vitalType: "temperature",
          label: "Temp",
          value: String(vital.value),
          unit: "°C",
        });
      }
    }

    // ── 1. Save each vital to patient DB ──────────────────────────────────────
    for (const obj of vitalsToSave) {
      try {
        await addPatientVitals(obj);
      } catch (err) {
        console.error(`NEWS2: failed to save ${obj.vitalType}`, err);
      }
    }

    // ── 2. ONE summary action for the actions panel card ──────────────────────
    const summaryPayload = {
      payload: JSON.stringify({
        vitalsDetails: {
          isNews2Session: true,
          vitalType: "NEWS2 Assessment",
          vitalTypeKey: "news2assessment",
          news2Score,
          riskLabel,
          recordedAt,
          patientId: base.patientId,
          news2Vitals: news2DisplayVitals,
        },
        timestamp: recordedAt,
        actionPerformedAt: recordedAt,
      }),
      actionName: ActionType[5],
      actionNumber: "5",
      isPerformed: true,
      performedAt: recordedAt,
    };

    try {
      await updateConsultationActions({
        consultationId,
        payload: summaryPayload,
      });
      await SaveAIChatMessage({
        ConsultationChat: JSON.stringify([
          {
            sender: "success",
            message: "NEWS2 assessment recorded.",
            actionId: 5,
            details: summaryPayload,
          },
        ]),
        ConsultationId: consultationId,
      });
      setVitalsDetails(summaryPayload);
      setIncrementVitalsCompleted(true);
    } catch (err) {
      console.error("NEWS2: failed to save summary action", err);
    }

    // ── 3. Single refresh after all saves ─────────────────────────────────────
    try {
      await getPatientConsultationByConsultationId(consultationId);
      await getPatientVitals(selectedPatientDetails?.patientID);
      await GetPatientVitalsSummaryByPatientId(
        selectedPatientDetails?.patientID,
      );
    } catch (err) {
      console.error("NEWS2: refresh error", err);
    }

    setIsNews2Saving(false);
    onRemoveManualCalculator?.("news2");
  };

  const groupedActions = React.useMemo(() => {
    const groups: Record<string, any> = {};

    const shouldHideDraft = (chat) => {
      try {
        const rawDetails = chat.details;
        const draftStatusValue =
          rawDetails?.draftStatus ||
          (rawDetails?.payload
            ? JSON.parse(rawDetails.payload)?.draftStatus
            : undefined);

        if (draftStatusValue === DraftStatus.Completed) return true;
      } catch {
        // ignore parse errors
      }

      return false;
    };
    patientAIChatMessages?.forEach((msg) => {
      if (msg.sender !== "success") return;

      // Handle drafts (actionId 11) - check if they should be hidden
      const isDraftEntry = msg.actionId === 11 || msg.actionId === "11";

      if (isDraftEntry && shouldHideDraft(msg)) {
        // A completion marker arrived — clear any pending draft group that was
        // already built from an earlier entry in the same chat replay.
        const draftConfig = ACTION_GROUP_CONFIG[msg.actionId];
        if (draftConfig && groups[draftConfig.key]) {
          delete groups[draftConfig.key];
        }
        return;
      }

      const config = ACTION_GROUP_CONFIG[msg.actionId];

      if (!config) return;

      if (!groups[config.key]) {
        groups[config.key] = {
          groupKey: config.key,
          title: config.title,
          actionId: msg.actionId,
          items: [],
        };
      }

      // logic for draft replacement: only keep the most recent draft item
      if (isDraftEntry) {
        groups[config.key].items = [
          {
            ...msg,
            isDraft: true,
            displayTimestamp: msg.timestamp
              ? new Date(msg.timestamp)
              : new Date(),
          },
        ];
        return;
      }

      // Extract the correct timestamp based on action type
      let recordedTimestamp = null;

      try {
        if (msg.actionId === 1 && msg.details?.payload) {
          // Prescription
          const payload = JSON.parse(msg.details.payload);
          recordedTimestamp = payload.recordedAt;
        } else if (msg.actionId === 3 && msg.details) {
          // Referral
          recordedTimestamp = msg.details.recordedAt;
        } else if (msg.actionId === 4 && msg.details?.payload) {
          // Appointment
          const payload = JSON.parse(msg.details.payload);
          recordedTimestamp = payload.createdAt;
        } else if (msg.actionId === 5 && msg.details?.payload) {
          // Vitals
          const payload = JSON.parse(msg.details.payload);
          recordedTimestamp = payload.vitalsDetails?.recordedAt;
        } else if (msg.actionId === 6 && msg.details?.payload) {
          // Vaccine
          const payload = JSON.parse(msg.details.payload);
          recordedTimestamp = payload?.recordedAt;
        } else if (msg.actionId === 8 && msg.details?.payload) {
          // Documents
          const payload = JSON.parse(msg.details.payload);
          recordedTimestamp = payload.recordedAt;
        } else if (msg.actionId === 9 && msg.details) {
          // Signatures
          recordedTimestamp = msg.details.signedAt;
        } else if (msg.actionId === 13 && msg.details) {
          // Referral
          recordedTimestamp = msg.details.recordedAt;
        }
      } catch (e) {
        console.warn(
          `Failed to parse timestamp for action ${msg.actionId}:`,
          e,
        );
      }

      // Use the extracted recordedAt, or fallback to displayTimestamp or current time
      const displayTimestamp = recordedTimestamp
        ? new Date(recordedTimestamp)
        : msg.timestamp
        ? new Date(msg.timestamp)
        : new Date();

      groups[config.key].items.push({
        ...msg,
        isDraft: isDraftEntry, // Set isDraft based on actionId
        displayTimestamp,
        recordedAt: recordedTimestamp, // Store the original recordedAt for reference
      });
    });

    // 2️⃣ drafts (pharmacistActions)
    pharmacistActions
      ?.filter(
        (a) => a.type === "prescription-draft" || a.type === "vaccine-draft",
      )
      .forEach((a) => {
        const config = ACTION_GROUP_CONFIG[a.type];
        if (!config) return;

        if (!groups[config.key]) {
          groups[config.key] = {
            groupKey: config.key,
            title: config.title,
            actionId: a.type,
            items: [],
          };
        }

        groups[config.key].items.push({
          actionId: a.type,
          details: a.data,
          isDraft: true,
          displayTimestamp: a.timestamp ? new Date(a.timestamp) : new Date(),
        });
      });
    // 3️⃣ consultationData.actions se vaccine/prescription drafts
    (consultationData?.actions || []).forEach((action: any) => {
      if (
        action.actionName !== "CreateVaccineDraft" &&
        action.actionName !== "CreatePrescriptionDraft"
      )
        return;

      const actionKey = action.actionName === "CreateVaccineDraft" ? 12 : 11;
      const config = ACTION_GROUP_CONFIG[actionKey];
      if (!config) return;

      const alreadyExists = patientAIChatMessages?.some(
        (msg) =>
          msg.sender === "success" &&
          (msg.actionId === actionKey || msg.actionId === String(actionKey)),
      );
      if (alreadyExists) return;

      if (!groups[config.key]) {
        groups[config.key] = {
          groupKey: config.key,
          title: config.title,
          actionId: actionKey,
          items: [],
        };
      }

      groups[config.key].items = [
        {
          actionId: actionKey,
          sender: "success",
          details: {
            payload: action.payload,
            actionName: action.actionName,
            actionNumber: String(actionKey),
            isPerformed: action.isPerformed,
            draftStatus: DraftStatus.Pending,
          },
          isDraft: true,
          displayTimestamp: new Date(),
        },
      ];
    });

    // 4️⃣ Treatment plans from consultationData.actions (survives refresh)
    (consultationData?.actions || []).forEach((action: any) => {
      if (
        action.actionName !== "CreateTreatmentPlan" &&
        action.actionName !== "UpdateTreatmentPlan"
      )
        return;

      const actionKey = 15;
      const config = ACTION_GROUP_CONFIG[actionKey];
      if (!config) return;

      let planId: string | null = null;
      try {
        const parsed = JSON.parse(action.payload || "{}");
        planId = parsed.treatmentPlanId || null;
      } catch {}

      const alreadyInChat = patientAIChatMessages?.some((msg) => {
        if (msg.sender !== "success") return false;
        if (msg.actionId !== 15 && msg.actionId !== "15") return false;
        if (!planId) return false;
        try {
          const d = msg.details;
          if (d?.treatmentPlanId === planId) return true;
          if (d?.payload) {
            const p = JSON.parse(d.payload);
            return (
              p?.treatmentPlanId === planId ||
              p?.treatmentPlan?.treatmentPlanId === planId
            );
          }
        } catch {}
        return false;
      });

      // ← KEY FIX: instead of skipping when alreadyInChat, OVERRIDE the stale
      // chat message snapshot with the fresh payload from consultationData.actions.
      // (updateTreatmentPlan mutates the CreateTreatmentPlan payload in-place in the
      // DB, so consultationData.actions is always the source of truth.)
      if (alreadyInChat) {
        if (groups[config.key]) {
          const groupItemIdx = groups[config.key].items.findIndex(
            (item: any) => {
              try {
                const p = JSON.parse(item.details?.payload || "{}");
                return (
                  p?.treatmentPlanId === planId ||
                  p?.treatmentPlan?.treatmentPlanId === planId
                );
              } catch {
                return false;
              }
            },
          );
          if (groupItemIdx !== -1) {
            groups[config.key].items[groupItemIdx] = {
              ...groups[config.key].items[groupItemIdx],
              details: {
                payload: action.payload,
                actionName: action.actionName,
                actionNumber: String(actionKey),
                isPerformed: action.isPerformed,
              },
            };
          }
        }
        return;
      }

      if (!groups[config.key]) {
        groups[config.key] = {
          groupKey: config.key,
          title: config.title,
          actionId: actionKey,
          items: [],
        };
      }

      const alreadyInGroup =
        planId &&
        groups[config.key].items.some((item: any) => {
          try {
            const p = JSON.parse(item.details?.payload || "{}");
            return (
              p?.treatmentPlanId === planId ||
              p?.treatmentPlan?.treatmentPlanId === planId
            );
          } catch {
            return false;
          }
        });
      if (alreadyInGroup) return;

      groups[config.key].items.push({
        actionId: actionKey,
        sender: "success",
        details: {
          payload: action.payload,
          actionName: action.actionName,
          actionNumber: String(actionKey),
          isPerformed: action.isPerformed,
        },
        displayTimestamp: new Date(),
      });
    });
    // 3️⃣ sort items inside each group by displayTimestamp (newest first for display)
    Object.values(groups).forEach((group: any) => {
      group.items.sort(
        (a, b) =>
          new Date(b.displayTimestamp).getTime() -
          new Date(a.displayTimestamp).getTime(),
      );
    });

    // 4️⃣ Sort groups by their most recent item (newest first)
    return Object.values(groups).sort((a: any, b: any) => {
      const aLatest = a.items[0]?.displayTimestamp || new Date(0);
      const bLatest = b.items[0]?.displayTimestamp || new Date(0);
      return new Date(bLatest).getTime() - new Date(aLatest).getTime();
    });
  }, [patientAIChatMessages, pharmacistActions, consultationData]);

  const taskActions = useMemo(() => {
    const parseTasks = (payload: any) => {
      try {
        const parsed = JSON.parse(payload);
        return parsed?.tasks || [];
      } catch (e) {
        console.error("Error parsing tasks payload:", e);
        return [];
      }
    }; // Source 1: AI Tasks from chat (actionId 14)

    const aiTasks = (() => {
      if (!patientAIChatMessages?.length) {
        return [];
      }
      const taskMessage = patientAIChatMessages.find(
        (msg) => String(msg.actionId) === "14",
      );
      if (!taskMessage?.details?.payload) {
        return [];
      }
      return parseTasks(taskMessage.details.payload);
    })(); // Source 2: Consultation Data tasks

    const consultationDataTasks = (() => {
      const alreadyInChat = patientAIChatMessages?.some(
        (msg) => String(msg.actionId) === "14",
      );
      if (alreadyInChat) {
        return [];
      }
      const taskAction = (consultationData?.actions || []).find(
        (a: any) => a.actionName === "CreateTask",
      );
      if (!taskAction?.payload) {
        return [];
      }
      return parseTasks(taskAction.payload);
    })(); // Source 3: Consultation template actions

    const consultationActions = [
      ...(selectedTemplate?.consultationActions || []),
      ...(selectedPatientConsultation?.template?.consultationActions || []),
    ];

    const allTasks = [
      ...aiTasks,
      ...consultationDataTasks,
      ...consultationActions,
    ]; // Deduplicate and add isCompleted state from Set

    const uniqueTasks = allTasks
      .filter(
        (action: any, index: number, self: any[]) =>
          action?.label &&
          index === self.findIndex((a) => a?.label === action?.label),
      )
      .map((action: any) => {
        const isInSet = completedTreatmentActions.has(action.label);
        const fieldCompleted =
          action.isCompleted || action.completed || action.enabled;
        const finalIsCompleted = isInSet || fieldCompleted || false;
        return {
          ...action,
          isCompleted: finalIsCompleted,
        };
      });

    return uniqueTasks;
  }, [
    patientAIChatMessages,
    selectedTemplate,
    selectedPatientConsultation,
    consultationData,
    completedTreatmentActions,
  ]);

  const shouldShowCompletedActions =
    manualCalculators.length === 0 &&
    ((patientAIChatMessages &&
      patientAIChatMessages.some((chat) => chat.sender === "success")) ||
      (pharmacistActions &&
        pharmacistActions.some(
          (a) => a.type === "prescription-draft" || a.type === "vaccine-draft",
        )) ||
      groupedActions.length > 0);

  const isActionsEmpty =
    suggestedActions.length === 0 &&
    manualCalculators.length === 0 &&
    patientAIChatMessages?.filter((chat) => chat.sender === "success")
      .length === 0 &&
    !pharmacistActions?.some(
      (a) =>
        (a.type as any) === "prescription-draft" ||
        (a.type as any) === "vaccine-draft",
    );
  const hasActiveTreatmentPlans =
    Array.isArray(patientActiveTreatmentPlans) &&
    patientActiveTreatmentPlans.length > 0;
  const isTreatmentPlanLinked =
    !!linkedTreatmentPlanId ||
    !!isTreatmenPlanCreated ||
    (consultationData?.actions || []).some(
      (action: any) => action?.actionName === "CreateTreatmentPlan",
    ) ||
    patientAIChatMessages?.some(
      (msg) => msg?.sender === "success" && String(msg?.actionId) === "15",
    );
  const isConsultationCompleted =
    selectedPatientConsultation?.consultationStatus === 1;

  const mapAIResponseToDraft = (aiResponse: any) => {
    const vitalTypeMap: Record<string, string> = {
      WEIGHT: "weight",
      BLOOD_PRESSURE: "blood-pressure",
      HEART_RATE: "heart-rate",
      TEMPERATURE: "temperature",
      HEIGHT: "height",
      BMI: "bmi",
      OXYGEN_SATURATION: "oxygen-saturation",
      BLOOD_GLUCOSE: "blood-glucose",
    };

    const parseDurationMinutes = (durationStr: string): string => {
      if (!durationStr) return "30";
      const match = durationStr.match(/(\d+)/);
      return match ? match[1] : "30";
    };

    // A goal is only "complete" if it has all required fields for GoalsSection
    const isGoalComplete = (g: any) =>
      !!(g.description || g.goal) && !!g.targetValue && !!g.targetDate;

    const completeGoals = (aiResponse.treatment_Goals || []).filter(
      isGoalComplete,
    );
    const incompleteGoals = (aiResponse.treatment_Goals || []).filter(
      (g: any) => !isGoalComplete(g),
    );

    return {
      name: aiResponse.title || "",
      description: aiResponse.description || "",
      startDate: new Date().toISOString().split("T")[0],
      endDate: "",
      status: "active" as const,

      // Only fully complete goals get pre-added
      goals: completeGoals.map((g: any) => ({
        id: `goal-${Date.now()}-${Math.random()
          .toString(36)
          .substr(2, 9)}`,
        description: g.description || g.goal || "",
        linkedVitalType: vitalTypeMap[g.vital_Type?.toUpperCase()] || undefined,
        targetValue: String(g.targetValue),
        targetDate: g.targetDate || "",
        status: "pending" as const,
        notes: g.unit ? `Unit: ${g.unit}` : "",
        createdAt: new Date().toISOString(),
      })),

      // Incomplete goals → stored separately to pre-fill the add-goal form
      pendingGoals: incompleteGoals.map((g: any) => ({
        description: g.description || g.goal || "",
        linkedVitalType: vitalTypeMap[g.vital_Type?.toUpperCase()] || undefined,
        notes: g.unit ? `Unit: ${g.unit}` : "",
      })),

      // AI never provides serviceId/pharmacist/time so appointments
      // always go into the form, never pre-added
      followUpAppointments: [],
      pendingAppointments: (aiResponse.follow_Up_Appointments || []).map(
        (a: any) => ({
          notes: [a.timing, a.reason].filter(Boolean).join(" — "),
          duration: parseDurationMinutes(a.duration),
        }),
      ),

      linkedPrescriptions: [],
      subscriptionPlan: undefined,
      isDraft: true,
      savedAt: new Date().toISOString(),
    };
  };

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <div className="flex-1 p-0 overflow-y-auto">
        {hasActiveTreatmentPlans &&
          !isTreatmentPlanLinked &&
          !isConsultationCompleted && (
            <div
              className={`rounded-lg p-4 border-2 border-dashed mt-3 mb-4 mx-4 ${
                isDarkMode
                  ? "bg-gray-800/50 border-gray-600"
                  : "bg-gray-50 border-gray-300"
              }`}
            >
              <div className="flex items-start gap-3">
                <div
                  className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    isDarkMode ? "bg-gray-700" : "bg-gray-100"
                  }`}
                >
                  <FileText
                    className={`w-5 h-5 ${
                      isDarkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  />
                </div>
                <div className="flex-1">
                  <h3
                    className={`text-sm font-semibold mb-1 ${
                      isDarkMode ? "text-gray-200" : "text-gray-800"
                    }`}
                  >
                    Link to Treatment Plan
                  </h3>
                  <p
                    className={`text-xs mb-3 ${
                      isDarkMode ? "text-gray-400" : "text-gray-600"
                    }`}
                  >
                    This consultation is not linked to a treatment plan. Link it
                    to track progress and follow appointment actions.
                  </p>
                  <button
                    onClick={() => setLinkTreatmentPlanDialog(true)}
                    className="px-3 py-1.5 rounded-md text-xs font-medium transition-colors bg-black hover:bg-gray-800 text-white"
                  >
                    Link to Treatment Plan
                  </button>
                </div>
              </div>
            </div>
          )}

        {/* Manually selected calculator widgets */}
        {manualCalculators.length > 0 && (
          <div className="mb-6 space-y-4">
            {manualCalculators.map((calculator) =>
              calculator.id === "news2" ? (
                <div
                  key={calculator.id}
                  className={`border rounded-lg overflow-hidden ${
                    isDarkMode ? "border-gray-700" : "border-gray-200"
                  }`}
                >
                  {/* Header */}
                  <div
                    className={`p-1 flex items-center justify-between border-b sticky top-0 z-10 ${
                      isDarkMode
                        ? "bg-[#121212] border-gray-700"
                        : "bg-white border-gray-200"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0">
                        <Calculator className="w-5 h-5 text-purple-600" />
                      </div>
                      <h3 className="text-sm font-semibold">
                        NEWS2 Calculator
                      </h3>
                    </div>
                    <button
                      onClick={() => onRemoveManualCalculator?.(calculator.id)}
                      className={`flex items-center gap-1 p-1.5 rounded-md transition-colors ${
                        isDarkMode
                          ? "hover:bg-gray-700 text-gray-400 hover:text-gray-200"
                          : "hover:bg-gray-200 text-gray-600 hover:text-gray-900"
                      }`}
                    >
                      <X className="w-4 h-4" />
                      <span className="text-sm">Dismiss</span>
                    </button>
                  </div>
                  {/* Form with padding */}
                  <div className="px-4 py-4">
                    <NEWS2Calculator
                      isDarkMode={isDarkMode}
                      onComplete={handleNEWS2Complete}
                      onCancel={() => onRemoveManualCalculator?.(calculator.id)}
                      isSaving={isNews2Saving}
                    />
                  </div>
                </div>
              ) : (
                <CalculatorWidget
                  key={calculator.id}
                  calculator={calculator}
                  isDarkMode={isDarkMode}
                  patientInfo={selectedPatient}
                  onCalculatorComplete={() => {}}
                  onCreatePrescriptionDraft={handleCreatePrescriptionDraft}
                  onCreateVaccineDraft={handleCreateVaccineDraft}
                  onSaveVitals={handleSaveVitals}
                  onClose={() => onRemoveManualCalculator?.(calculator.id)}
                />
              ),
            )}
          </div>
        )}
        {/* Task Actions - Always Show */}
        {/* {taskActions.length > 0 && (
          <div className="mx-4 mb-6">
            <h3
              className={`text-sm font-medium mb-3 flex items-center gap-2 ${
                isDarkMode ? "text-gray-400" : "text-gray-500"
              }`}
            >
              <Target className="w-4 h-4 text-cyan-600" />
              Appointment Actions
            </h3>

            <div
              className={`rounded-lg p-3 ${
                isDarkMode
                  ? "bg-cyan-900/20 border border-cyan-800"
                  : "bg-cyan-50 border border-cyan-200"
              }`}
            >
              {taskActions.map((action) => {
                const isCompleted = completedTreatmentActions.has(action.label);

                return (
                  <div
                    key={action.label}
                    onClick={() => {
                      setCompletedTreatmentActions((prev) => {
                        const newSet = new Set(prev);
                        if (newSet.has(action.label)) {
                          newSet.delete(action.label);
                        } else {
                          newSet.add(action.label);
                        }
                        return newSet;
                      });
                    }}
                    className={`flex items-center gap-3 p-2 rounded transition-all cursor-pointer ${
                      isCompleted
                        ? isDarkMode
                          ? "bg-green-900/30 text-green-400"
                          : "bg-green-100 text-green-700"
                        : isDarkMode
                          ? "hover:bg-gray-600/50"
                          : "hover:bg-white hover:shadow-sm"
                    }`}
                  >
                    <div
                      className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                        isCompleted
                          ? "bg-green-600 border-green-600"
                          : isDarkMode
                            ? "border-gray-500"
                            : "border-gray-300"
                      }`}
                    >
                      {isCompleted && <Check className="w-3 h-3 text-white" />}
                    </div>

                    <span
                      className={`text-sm font-medium flex-1 ${
                        isCompleted ? "line-through opacity-70" : ""
                      }`}
                    >
                      {action.label}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )} */}
        {taskActions.length > 0 && manualCalculators.length === 0 && (
          <div className="p-4">
            <h3
              className={`text-sm font-medium mb-3 flex items-center gap-2 ${
                isDarkMode ? "text-gray-400" : "text-gray-500"
              }`}
            >
              <Target className="w-4 h-4 text-cyan-600" />
              Appointment Actions
            </h3>

            <div
              className={`rounded-lg p-3 ${
                isDarkMode
                  ? "bg-cyan-900/20 border border-cyan-800"
                  : "bg-cyan-50 border border-cyan-200"
              }`}
            >
              {taskActions.map((action) => {
                // Set is the ONLY source of truth - not action.isCompleted
                const isCompleted = completedTreatmentActions.has(action.label);

                return (
                  <div
                    key={action.label}
                    onClick={async () => {
                      // Toggle: if currently completed, make it NOT completed, and vice versa
                      const newValue = !isCompleted; // :white_check_mark: Update UI immediately (optimistic)

                      toggleCompletedTaskForConsultation(
                        consultationId,
                        action.label,
                      );

                      try {
                        await updateTaskActionStatus({
                          consultationId: consultationId,
                          label: action.label,
                          enabled: newValue,
                        });
                      } catch (err) {
                        console.error("Update failed:", err);
                      }
                    }}
                    className={`flex items-center gap-3 p-2 rounded transition-all cursor-pointer ${
                      isCompleted
                        ? isDarkMode
                          ? "bg-green-900/30 text-green-400"
                          : "bg-green-100 text-green-700"
                        : isDarkMode
                        ? "hover:bg-gray-600/50"
                        : "hover:bg-white hover:shadow-sm"
                    }`}
                  >
                    <div
                      className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                        isCompleted
                          ? "bg-green-600 border-green-600"
                          : isDarkMode
                          ? "border-gray-500"
                          : "border-gray-300"
                      }`}
                    >
                      {isCompleted && <Check className="w-3 h-3 text-white" />}
                    </div>

                    <span
                      className={`text-sm font-medium flex-1 ${
                        isCompleted ? "line-through opacity-70" : ""
                      }`}
                    >
                      {action.label}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        {/* Suggestions Section */}
        {suggestedActions && suggestedActions.length > 0 && (
          <div className="p-4 mb-6">
            <h3
              className={`text-sm font-medium mb-3 flex items-center gap-2 ${
                isDarkMode ? "text-gray-400" : "text-gray-500"
              }`}
            >
              <Sparkles className="w-4 h-4" />
              Suggestions
            </h3>
            <div
              className={`rounded-lg p-3 space-y-2 ${
                isDarkMode ? "bg-gray-700/50" : "bg-blue-50"
              }`}
            >
              {suggestedActions.map((action: any, idx: number) => {
                const actionNum = String(
                  action.ActionNumber ?? action.actionNumber ?? "",
                ).trim();
                // Payment action (actionNum "2") is temporarily hidden from suggestions
                if (actionNum === "2") return null;
                // Check if this action exists in patientAIChatMessages with sender === "success"
                const isCompleted =
                  patientAIChatMessages?.some(
                    (chat) =>
                      chat.sender === "success" &&
                      String(chat.actionId) === actionNum,
                  ) ?? false;

                return (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`flex items-center gap-3 p-2 rounded transition-all ${
                      isCompleted
                        ? isDarkMode
                          ? "bg-green-900/30 text-green-400"
                          : "bg-green-100 text-green-700"
                        : isDarkMode
                        ? "hover:bg-gray-600/50 cursor-pointer"
                        : "hover:bg-white cursor-pointer hover:shadow-sm"
                    }`}
                    onClick={() => {
                      if (!isCompleted) {
                        handleSuggestionClick(actionNum);
                      }
                    }}
                  >
                    <div
                      className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                        isCompleted
                          ? "bg-green-600 border-green-600"
                          : isDarkMode
                          ? "border-gray-500"
                          : "border-gray-300"
                      }`}
                    >
                      {isCompleted && <Check className="w-3 h-3 text-white" />}
                    </div>

                    <span
                      className={`text-sm font-medium flex-1 ${
                        isCompleted
                          ? "line-through opacity-70"
                          : isDarkMode
                          ? "text-white"
                          : "text-gray-900"
                      }`}
                    >
                      {action.ActionName || action.actionName}
                    </span>
                  </motion.div>
                );
              })}
              {!isTreatmentPlanLinked && (
                <button
                  disabled={isGeneratingAIPlan}
                  onClick={async () => {
                    const summary =
                      consultationData?.template?.consultationSummary;
                    if (!summary) {
                      console.warn("No consultation summary found");
                      return;
                    }
                    setIsGeneratingAIPlan(true);
                    try {
                      const result = await generateAITreatmentPlan({
                        ConsultationSummary:
                          patientSummaryFromState.SummaryText ||
                          consultationData?.consultationSummary,
                      });
                      if (result) {
                        const mappedDraft = mapAIResponseToDraft(result);
                        saveTreatmentPlanDraft(consultationId, mappedDraft);
                      }
                    } catch (err) {
                      console.error("Failed to generate AI plan:", err);
                    } finally {
                      setIsGeneratingAIPlan(false);
                    }
                  }}
                  className={`w-full px-2 py-1.5 rounded transition-all duration-200 mt-3 ${
                    isDarkMode
                      ? "text-purple-400 hover:text-purple-300 hover:bg-purple-900/20"
                      : "text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                  } text-xs font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1.5 group`}
                >
                  {isGeneratingAIPlan ? (
                    <>
                      <Loader className="w-3.5 h-3.5 animate-spin" />
                      <span>Generating...</span>
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-3.5 h-3.5" />
                      <span>Generate AI Treatment Plan</span>
                    </>
                  )}
                </button>
              )}
            </div>

            {/* Generate AI Treatment Plan Button */}
          </div>
        )}
        {currentTreatmentPlanDraft && !isConsultationCompleted && (
          <div className="p-4 mb-4">
            <h4
              className={`text-sm font-medium mb-3 flex items-center gap-2 ${
                isDarkMode ? "text-gray-400" : "text-gray-500"
              }`}
            >
              {/* reuse the same FileText icon already imported at the top */}
              <FileText className="w-4 h-4 text-amber-500" />
              Treatment Plan Draft
            </h4>

            <TreatmentPlanDraftActionItem
              draft={currentTreatmentPlanDraft}
              isDarkMode={isDarkMode}
              onEdit={() => setShowTreatmentPlanDraftModal(true)}
              onDelete={() => clearTreatmentPlanDraft(consultationId)}
            />

            {/* Modal opened when the draft card is clicked */}
            <TreatmentPlanModal
              open={showTreatmentPlanDraftModal}
              onOpenChange={setShowTreatmentPlanDraftModal}
              /* Pass draft data so the modal pre-populates every field */
              draftData={currentTreatmentPlanDraft.data}
              editMode={false}
              isDarkMode={isDarkMode}
              selectedPatient={selectedPatientDetails}
              consultationId={consultationId}
              onSave={(_savedPlan) => {
                // Plan was fully saved — wipe the draft so the card disappears
                clearTreatmentPlanDraft(consultationId);
                setShowTreatmentPlanDraftModal(false);
              }}
              onSaveDraft={(updatedDraftData) => {
                // User closed modal again without saving — update draft in Redux
                saveTreatmentPlanDraft(consultationId, updatedDraftData);
              }}
            />
          </div>
        )}
        {shouldShowCompletedActions && (
          <div className="p-4 mb-6">
            <h4
              className={`text-sm font-medium mb-4 flex items-center gap-2 ${
                isDarkMode ? "text-gray-400" : "text-gray-500"
              }`}
            >
              <CheckCircle className="w-4 h-4" />
              Completed Actions
            </h4>
            <div className="space-y-3">
              {groupedActions.length > 0 && (
                <div className="space-y-4">
                  {groupedActions.map((group: any) => (
                    <ActionGroupCard
                      key={group.groupKey}
                      isDarkMode={isDarkMode}
                      group={group}
                      onDeleteAction={onDeleteAction}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* 🔹 PAYMENT STATUS CARD (STATIC DISPLAY) */}
        {paymentAuditData && Object.keys(paymentAuditData).length > 0 && (
          <div className="p-4 mb-6">
            <h4
              className={`text-sm font-medium mb-2 flex items-center gap-2 ${
                isDarkMode ? "text-gray-400" : "text-gray-500"
              }`}
            >
              <Info className="w-4 h-4" />
              Payment Information
            </h4>

            <div
              className={`rounded-lg p-4 space-y-3 transition-all ${
                paymentStatus?.hasFullPayment
                  ? isDarkMode
                    ? "bg-green-900/30 border-2 border-green-600/50"
                    : "bg-green-50 border-2 border-green-300"
                  : isDarkMode
                  ? "bg-blue-900/30 border-2 border-blue-600/50"
                  : "bg-blue-50 border-2 border-blue-300"
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center flex-1 gap-3">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      paymentStatus?.hasFullPayment
                        ? isDarkMode
                          ? "bg-green-600/20"
                          : "bg-green-100"
                        : isDarkMode
                        ? "bg-blue-600/20"
                        : "bg-blue-100"
                    }`}
                  >
                    {paymentStatus?.hasFullPayment ? (
                      <CheckCircle
                        className={`w-5 h-5 ${
                          isDarkMode ? "text-green-400" : "text-green-600"
                        }`}
                      />
                    ) : (
                      <CreditCard
                        className={`w-5 h-5 ${
                          isDarkMode ? "text-blue-400" : "text-blue-600"
                        }`}
                      />
                    )}
                  </div>

                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <p
                        className={`text-sm font-semibold ${
                          isDarkMode ? "text-gray-200" : "text-gray-800"
                        }`}
                      >
                        {getPaymentTypeLabel(paymentStatus)} Received
                      </p>

                      <Badge
                        className={`text-xs ${
                          paymentStatus?.hasFullPayment
                            ? "bg-green-600 text-white"
                            : "bg-blue-600 text-white"
                        }`}
                      >
                        £{paymentStatus?.totalPaid.toFixed(2)}
                      </Badge>
                    </div>

                    <div className="space-y-0.5">
                      {getPaymentDetails(paymentStatus).map(
                        (detail: string, idx: number) => (
                          <p
                            key={idx}
                            className={`text-xs ${
                              isDarkMode ? "text-gray-400" : "text-gray-600"
                            }`}
                          >
                            {detail}
                          </p>
                        ),
                      )}

                      <p
                        className={`text-xs ${
                          isDarkMode ? "text-gray-400" : "text-gray-600"
                        }`}
                      >
                        Method: {paymentStatus?.paymentMethod}
                      </p>

                      <p className="text-xs text-gray-500">
                        {new Date(paymentStatus?.paymentDate).toLocaleString()}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div
                className={`pt-3 border-t ${
                  isDarkMode ? "border-gray-700" : "border-gray-200"
                }`}
              >
                <div className="flex items-center justify-between">
                  <p
                    className={`text-xs ${
                      isDarkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    Payment details (view only)
                  </p>
                  <div
                    className={`text-xs font-medium ${
                      paymentStatus?.hasFullPayment
                        ? isDarkMode
                          ? "text-green-400"
                          : "text-green-600"
                        : isDarkMode
                        ? "text-blue-400"
                        : "text-blue-600"
                    }`}
                  >
                    {paymentStatus?.hasFullPayment
                      ? "Paid in Full"
                      : "Partial Payment"}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Empty State */}

        <LinkTreatmentPlanDialog
          open={isLinkTreatmentPlanDialogOpen}
          onOpenChange={(open) => setLinkTreatmentPlanDialog(open)}
          activePlans={patientActiveTreatmentPlans || []}
          onSelectPlan={() => {
            // TODO later
          }}
          onSkip={() => {
            // TODO later
          }}
          isDarkMode={isDarkMode}
          fromConsultataion={true}
        />
      </div>
    </div>
  );
};
